import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFetcher } from '@remix-run/react';
import { useLatest } from 'ahooks';
import type { BasicPlace } from '~/modules/location/type';
import { isValidCoordinates } from '~/modules/professional/hook/use-locations-sort-by-distance';
import { useCurrentCoordinates } from '~/modules/location/hook/use-current-coordinates';
import type { loader } from '~/routes/remix-api.location.place-by-coordinate';

export interface IUseCurrentLocationResult {
    currentLocationDisabled: boolean;
    currentLocation: BasicPlace | null;
    loading: boolean;
    getCurrentLocation: () => void;
}

const useCurrentLocation = (): IUseCurrentLocationResult => {
    const {
        userCoordinates,
        findUserCoordinates,
        loaded: coordinatesLoaded,
    } = useCurrentCoordinates();
    const fetcher = useFetcher<typeof loader>();
    const locationFetcher = useLatest(fetcher);
    const [loading, setLoading] = useState(false);

    const fetchCurrentLocation = useCallback(() => {
        if (!coordinatesLoaded) return;
        if (!userCoordinates || !isValidCoordinates(userCoordinates)) {
            setLoading(false);
            return;
        }
        locationFetcher.current.submit(
            {
                longitude: userCoordinates.Longitude.toString(),
                latitude: userCoordinates.Latitude.toString(),
            },
            {
                method: 'get',
                action: `/remix-api/location/place-by-coordinate`,
            }
        );
    }, [coordinatesLoaded, locationFetcher, userCoordinates]);

    useEffect(() => {
        if (locationFetcher.current.state !== 'idle') return;
        setLoading(false);
    }, [
        locationFetcher,
        locationFetcher.current.data,
        locationFetcher.current.state,
    ]);

    useEffect(() => {
        if (!coordinatesLoaded) return;
        fetchCurrentLocation();
    }, [userCoordinates, coordinatesLoaded, fetchCurrentLocation]);

    const getCurrentLocation = useCallback(() => {
        setLoading(true);
        findUserCoordinates();
    }, [findUserCoordinates]);

    const urlSafeLocation = useMemo<BasicPlace | null>(() => {
        if (fetcher.data) {
            // We can't expose latitude and longitude in the URL for privacy reasons
            return {
                // Do not use suburb for search location filter.
                // suburb: fetcher.data.suburb,
                district: fetcher.data.district ?? undefined,
                city: fetcher.data.city,
                region: fetcher.data.region,
                state: fetcher.data.state ?? undefined,
                country: fetcher.data.country ?? undefined,
            };
        }
        return null;
    }, [fetcher.data]);

    return {
        currentLocationDisabled:
            coordinatesLoaded && !isValidCoordinates(userCoordinates),
        currentLocation: urlSafeLocation,
        getCurrentLocation,
        loading,
    };
};

export { useCurrentLocation };
